import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as hiveCore from '@gitbook/hive-core';
import { TextInput } from '@gitbook/react-uikit';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    Label,
} from '@gitbook/spine-react';

import { ContentReferenceInput, useContentReference } from '../../../Components';

export type InsertLinkModalKind = 'external' | 'space';

/**
 * Modal to insert a link (external or internal) in the ToC.
 */
export function InsertLinkModal(props: {
    onSubmit: (page: hiveCore.RevisionPageLink) => void;
    isOpen: boolean;
}): React.ReactElement {
    const { isOpen, onSubmit } = props;

    const [contentRef, setContentRef] = React.useState<api.ContentRef | null>(null);
    const [title, setTitle] = React.useState('');
    const isTitleDirtyRef = React.useRef(false);
    const [error, setError] = React.useState<string>('');

    // Resets modal state when it is closed.
    // Necessary because the modal is not unmounted on close.
    React.useEffect(() => {
        if (!isOpen) {
            setContentRef(null);
            setTitle('');
            isTitleDirtyRef.current = false;
            setError('');
        }
    }, [isOpen]);

    const submit = async () => {
        if (contentRef) {
            onSubmit(hiveCore.createPageLink({ title, target: contentRef }));
        } else {
            setError(
                'Something went wrong. We could not create that link. Please refresh and try again.'
            );
        }
    };

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Insert link</Heading>
                <Description>Add a content or URL link to your table of contents.</Description>
            </DialogHeader>
            <DialogBody>
                <div className="flex flex-col gap-6">
                    <React.Suspense
                        fallback={
                            <div className="flex flex-col gap-2">
                                <Label>URL or content</Label>
                                <TextInput disabled value="Loading..." />
                            </div>
                        }
                    >
                        <ContentReferenceInput
                            autoFocus
                            label="URL or content"
                            size="large"
                            placeholder="Type a URL or search a space, page or anchor by title..."
                            value={contentRef}
                            onChangeValue={setContentRef}
                            accept={['space', 'page', 'anchor', 'url']}
                            feedback={
                                error
                                    ? {
                                          type: 'danger',
                                          message: error,
                                      }
                                    : { type: 'none' }
                            }
                        />
                    </React.Suspense>

                    <React.Suspense
                        fallback={
                            <div className="flex flex-col gap-2">
                                <Label>Title</Label>
                                <TextInput disabled value="Loading..." />
                            </div>
                        }
                    >
                        <ToCItemTitleInput
                            contentRef={contentRef}
                            title={title}
                            setTitle={setTitle}
                            isTitleDirtyRef={isTitleDirtyRef}
                            error={error}
                        />
                    </React.Suspense>
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button label="Insert" kind="primary" onPress={submit} disabled={!contentRef} />
            </DialogFooter>
        </Dialog>
    );
}

function ToCItemTitleInput(props: {
    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    contentRef: api.ContentRef | null;
    error?: string;
    isTitleDirtyRef: React.MutableRefObject<boolean>;
}) {
    const { title, setTitle, contentRef, error, isTitleDirtyRef } = props;
    const resolved = useContentReference(contentRef);

    // Init title with the resolved content reference title.
    React.useEffect(() => {
        if (contentRef && !isTitleDirtyRef.current && resolved.title) {
            setTitle(resolved.title);
        }
    }, [setTitle, resolved.title, contentRef, isTitleDirtyRef]);

    return (
        <TextInput
            label="Title"
            size="large"
            placeholder="Enter a title to display..."
            value={title}
            onChangeText={(text) => {
                isTitleDirtyRef.current = true;
                setTitle(text);
            }}
            feedback={
                error
                    ? {
                          type: 'danger',
                          message: error,
                      }
                    : { type: 'none' }
            }
            aria-invalid={!title || error ? 'true' : undefined}
            cleareable
        />
    );
}
